import { useMemo, useEffect } from "react";
import {
  stripAccents,
  shouldAutoSelect,
} from "./useSelectState.helpers";
import { useSelectQuerySync } from "./useSelectQuerySync";
import { useSelectMultiple } from "./useSelectMultiple";
import { useSelectFocus } from "./useSelectFocus";
import {
  useSelectCoreState,
  useSelectDerived,
  useSelectFiltered,
  useSelectCallbacks,
  useSelectOnSelect,
} from "./useSelectState.hooks";
import type { UseSelectStateParams } from "./useSelectState.types";

export type { SelectOption } from "./useSelectState.types";

/**
 * Auto-selects the first filtered option when auto-selection is enabled and the
 * current query/selection state warrants it.
 *
 * @param enableAutoSelection Whether auto-selection is active.
 * @param filteredOptions Currently visible options.
 * @param query The current search query.
 * @param selectedValue The currently selected value.
 * @param onSelect Callback invoked with the value to select.
 */
function useAutoSelectEffect(
  enableAutoSelection: boolean, filteredOptions: string[],
  query: string, selectedValue: string, onSelect: (v: string | null) => void,
) {
  useEffect(() => {
    if (!enableAutoSelection) return;
    if (shouldAutoSelect(filteredOptions, query, selectedValue)) onSelect(filteredOptions[0]);
  }, [filteredOptions, query, selectedValue, onSelect, enableAutoSelection]);
}

/**
 * Central state hook for the Select/MultiSelect components: composes core state,
 * query sync, filtering, callbacks, multi-select, selection, and focus into a
 * single view model consumed by the trigger and dropdown parts.
 *
 * @param params Select configuration (value, options, handlers, mode flags).
 * @returns The composed select state and handlers.
 */
export function useSelectState({
  value, options, normalizedData: rawNormalizedData, getDisplayNameForValue,
  filterFunction, maxResults, enableAutoSelection, searchable = true,
  clearable = false, allowDeselect = false, onChange, name,
  useTriggerMode = false, multiple = false, multipleValues, multipleSummary,
}: UseSelectStateParams) {
  const normalizedData = useMemo(
    () => rawNormalizedData ?? options.map((o) => ({ value: o, label: o })),
    [rawNormalizedData, options],
  );

  const core = useSelectCoreState(value, searchable, getDisplayNameForValue);
  const {
    selectedValue, setSelectedValue, isFocused, setIsFocused, query, setQuery,
    activeOptionIndex, setActiveOptionIndex, isOpen, setIsOpen, searchTerm, setSearchTerm,
  } = core;

  const { displayNameRef, ensureDisplayNameInQuery } = useSelectQuerySync({
    value, selectedValue, setSelectedValue, query, setQuery, isFocused, searchable, getDisplayNameForValue,
  });

  const { filteredOptions, filteredData } = useSelectFiltered({
    searchable, searchTerm, useTriggerMode, query, options, normalizedData, filterFunction, maxResults,
  });

  useEffect(() => { setActiveOptionIndex(searchable ? 0 : -1); }, [filteredOptions, searchable]);

  const { close, handleQueryChange, handleSearchTermChange, handleClear, toggleOpen } = useSelectCallbacks({
    setQuery, setIsFocused, setIsOpen, setSearchTerm, setActiveOptionIndex, setSelectedValue, searchable, onChange, name,
  });

  const { selectedValues, toggleValue, multiDisplayValue } = useSelectMultiple({
    multiple, multipleValues, multipleSummary, getDisplayNameForValue, onChange, name,
  });

  const onSelect = useSelectOnSelect({
    multiple, searchable, allowDeselect, toggleValue, onChange, name,
    getDisplayNameForValue, displayNameRef, setQuery, setSelectedValue,
    setIsFocused, setIsOpen, setSearchTerm, selectedValue, close,
  });

  useAutoSelectEffect(enableAutoSelection, filteredOptions, query, selectedValue, onSelect);

  const { inputValue, selectedOption, displayValue } = useSelectDerived(
    normalizedData, value, selectedValue, query, isFocused, getDisplayNameForValue,
  );

  const { onFocus, onBlur } = useSelectFocus({
    selectedValue, query, getDisplayNameForValue, setQuery, setIsFocused, displayNameRef,
  });

  return {
    selectedValue, selectedValues, toggleValue, multiDisplayValue,
    isFocused, setIsFocused, query, activeOptionIndex, setActiveOptionIndex,
    filteredOptions, filteredData, inputValue, onSelect, handleQueryChange,
    handleSearchTermChange, ensureDisplayNameInQuery, onFocus, onBlur,
    isOpen, setIsOpen, searchTerm, close, handleClear, selectedOption,
    displayValue, toggleOpen, stripAccents,
  };
}
